/**
 * Format file size in bytes to human-readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size (e.g., "1.5 MB", "234 KB")
 *
 * @example
 * formatFileSize(1024) // "1 KB"
 * formatFileSize(1536000) // "1.46 MB"
 */
export const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
};

export const MAX_DOCUMENT_UPLOAD_MB = 5120;

export const GENERIC_FILE_ICON = "file";

const ICON_BY_EXTENSION = {
  pdf: "file-pdf",
  doc: "file-word",
  docx: "file-word",
  dot: "file-word",
  dotx: "file-word",
  odt: "file-word",
  rtf: "file-word",
  csv: "file-csv",
  tsv: "file-csv",
  xls: "file-excel",
  xlsx: "file-excel",
  xlsm: "file-excel",
  xlsb: "file-excel",
  ods: "file-excel",
  ppt: "file-powerpoint",
  pptx: "file-powerpoint",
  pps: "file-powerpoint",
  ppsx: "file-powerpoint",
  odp: "file-powerpoint",
  txt: "file-lines",
  log: "file-lines",
  md: "file-lines",
  jpg: "file-image",
  jpeg: "file-image",
  png: "file-image",
  gif: "file-image",
  bmp: "file-image",
  webp: "file-image",
  svg: "file-image",
  tif: "file-image",
  tiff: "file-image",
  heic: "file-image",
  heif: "file-image",
  ico: "file-image",
  mp4: "file-video",
  m4v: "file-video",
  mov: "file-video",
  avi: "file-video",
  mkv: "file-video",
  webm: "file-video",
  wmv: "file-video",
  flv: "file-video",
  mpeg: "file-video",
  mpg: "file-video",
  "3gp": "file-video",
  mp3: "file-audio",
  wav: "file-audio",
  m4a: "file-audio",
  aac: "file-audio",
  ogg: "file-audio",
  oga: "file-audio",
  opus: "file-audio",
  flac: "file-audio",
  wma: "file-audio",
  aiff: "file-audio",
  zip: "file-archive",
  rar: "file-archive",
  "7z": "file-archive",
  tar: "file-archive",
  gz: "file-archive",
  tgz: "file-archive",
  bz2: "file-archive",
  xz: "file-archive",
  json: "file-code",
  xml: "file-code",
  html: "file-code",
  htm: "file-code",
  css: "file-code",
  js: "file-code",
  ts: "file-code",
  py: "file-code",
  php: "file-code",
  sql: "file-code",
  sh: "file-code",
  yml: "file-code",
  yaml: "file-code",
  dcm: "file-medical",
  dicom: "file-medical",
};

const ICON_BY_CATEGORY = {
  pdf: "file-pdf",
  image: "file-image",
  excel: "file-excel",
  csv: "file-csv",
  word: "file-word",
  powerpoint: "file-powerpoint",
  code: "file-code",
  video: "file-video",
  audio: "file-audio",
  archive: "file-archive",
  dicom: "file-medical",
  text: "file-lines",
};

const stripDuplicateSuffix = (name) => name.replace(/[[(]\d+[)\]]$/, "");

const lookupIcon = (map, key) =>
  key && Object.prototype.hasOwnProperty.call(map, key) ? map[key] : "";

export const getIconExtension = (filename) => {
  if (!filename) return "";

  const cleaned = stripDuplicateSuffix(String(filename).trim());
  const lastDotIndex = cleaned.lastIndexOf(".");
  if (lastDotIndex === -1) return "";

  return cleaned.substring(lastDotIndex + 1).toLowerCase();
};

const getIconNameFromMimeType = (mimeType) => {
  const type = String(mimeType).toLowerCase();

  if (type.startsWith("image/") || type === "image") return "file-image";
  if (type === "application/pdf" || type === "pdf") return "file-pdf";
  if (type === "text/csv" || type === "text/tab-separated-values") {
    return "file-csv";
  }
  if (type.includes("presentation") || type.includes("powerpoint")) {
    return "file-powerpoint";
  }
  if (type.includes("sheet") || type.includes("excel")) return "file-excel";
  if (type.includes("word") || type.includes("document")) return "file-word";
  if (type.includes("dicom")) return "file-medical";
  if (type.startsWith("video/") || type === "video") return "file-video";
  if (type.startsWith("audio/") || type === "audio") return "file-audio";
  if (
    type.includes("zip") ||
    type.includes("rar") ||
    type.includes("7z") ||
    type.includes("tar") ||
    type === "archive"
  ) {
    return "file-archive";
  }
  if (
    type.includes("html") ||
    type.includes("json") ||
    type.includes("xml") ||
    type === "code"
  ) {
    return "file-code";
  }
  if (type.startsWith("text/") || type === "text") return "file-lines";

  return "";
};

export const getDocumentIconName = (doc) => {
  if (!doc) return GENERIC_FILE_ICON;

  const byExtension = lookupIcon(
    ICON_BY_EXTENSION,
    getIconExtension(doc.original_file_name || doc.name)
  );
  if (byExtension) return byExtension;

  const byCategory = lookupIcon(
    ICON_BY_CATEGORY,
    String(doc.type || "").toLowerCase()
  );
  return byCategory || GENERIC_FILE_ICON;
};

/**
 * Get Font Awesome icon class based on file type
 * @param {string} fileType - MIME type, filename, or file extension
 * @returns {string} Font Awesome icon class
 *
 * @example
 * getFileIcon("image/png") // "fas fa-file-image"
 * getFileIcon("application/pdf") // "fas fa-file-pdf"
 * getFileIcon(".docx") // "fas fa-file-word"
 * getFileIcon("unknown.qqq") // "fas fa-file"
 */
export const getFileIcon = (fileType) => {
  if (!fileType) return `fas fa-${GENERIC_FILE_ICON}`;

  const name =
    lookupIcon(ICON_BY_EXTENSION, getIconExtension(fileType)) ||
    getIconNameFromMimeType(fileType) ||
    GENERIC_FILE_ICON;

  return `fas fa-${name}`;
};

/**
 * Truncate filename to specified length while preserving extension
 * @param {string} name - Original filename
 * @param {number} maxLength - Maximum length for the filename
 * @returns {string} Truncated filename with extension
 *
 * @example
 * truncateFileName("very-long-document-name.pdf", 20) // "very-long-doc....pdf"
 */
export const truncateFileName = (name, maxLength = 30) => {
  if (!name || name.length <= maxLength) return name;

  const lastDotIndex = name.lastIndexOf(".");
  if (lastDotIndex === -1) {
    // No extension, just truncate
    return name.substring(0, maxLength - 3) + "...";
  }

  const extension = name.substring(lastDotIndex);
  const nameWithoutExt = name.substring(0, lastDotIndex);

  if (extension.length >= maxLength - 3) {
    // Extension is too long, just truncate everything
    return name.substring(0, maxLength - 3) + "...";
  }

  const availableLength = maxLength - extension.length - 3; // 3 for "..."
  return nameWithoutExt.substring(0, availableLength) + "..." + extension;
};

/**
 * Drop the middle of a string that has no extension to protect — a folder name, a title.
 * `truncateFileName` end-truncates those, which loses the tail that tells similar names apart.
 *
 * @param {string} text
 * @param {number} maxLength - Total characters allowed, ellipsis included
 * @param {number} tail - Characters kept from the end
 * @returns {string}
 *
 * @example
 * truncateMiddle("asfasfasfajhfalshfkjahskjfajkshfkjash", 20) // "asfasfasf…fkjash"
 */
export const truncateMiddle = (text, maxLength = 40, tail = 10) => {
  if (!text || text.length <= maxLength) return text;

  const keepTail = Math.min(Math.max(4, tail), maxLength - 2);

  return `${text.slice(0, maxLength - keepTail - 1)}\u2026${text.slice(
    -keepTail
  )}`;
};

/**
 * Get file extension from filename or path
 * @param {string} filename - Filename or file path
 * @returns {string} File extension (lowercase, with dot)
 *
 * @example
 * getFileExtension("document.pdf") // ".pdf"
 * getFileExtension("image.PNG") // ".png"
 */
export const getFileExtension = (filename) => {
  if (!filename) return "";
  const lastDotIndex = filename.lastIndexOf(".");
  if (lastDotIndex === -1) return "";
  return filename.substring(lastDotIndex).toLowerCase();
};

/**
 * Check if file type is viewable in browser
 * @param {string} fileType - MIME type or file extension
 * @returns {boolean} True if file can be viewed in browser
 *
 * @example
 * isViewableFile("application/pdf") // true
 * isViewableFile("image/png") // true
 * isViewableFile("application/zip") // false
 */
export const isViewableFile = (fileType) => {
  if (!fileType) return false;

  const type = fileType.toLowerCase();
  const viewableTypes = [
    "image",
    "pdf",
    "excel",
    "word",
    "text",
    "video",
    "audio",
  ];

  return viewableTypes.some((viewableType) => type.includes(viewableType));
};

/**
 * Validate file size against maximum allowed
 * @param {File|number} file - File object or size in bytes
 * @param {number} maxSize - Maximum allowed size in bytes
 * @returns {{valid: boolean, message?: string}} Validation result
 *
 * @example
 * validateFileSize(myFile, 10 * 1024 * 1024) // { valid: true }
 * validateFileSize(largeFile, 1024) // { valid: false, message: "..." }
 */
export const validateFileSize = (file, maxSize) => {
  const size = typeof file === "number" ? file : file?.size || 0;

  if (size > maxSize) {
    return {
      valid: false,
      message: `File is too large. Maximum size is ${formatFileSize(maxSize)}.`,
    };
  }

  return { valid: true };
};

/**
 * Validate file type against allowed types
 * @param {File|string} file - File object or MIME type string
 * @param {string[]} allowedTypes - Array of allowed MIME types or extensions
 * @returns {{valid: boolean, message?: string}} Validation result
 *
 * @example
 * validateFileType(myFile, ["application/pdf", ".pdf"]) // { valid: true }
 * validateFileType(zipFile, ["application/pdf"]) // { valid: false, message: "..." }
 */
export const validateFileType = (file, allowedTypes = []) => {
  if (!allowedTypes || allowedTypes.length === 0) {
    return { valid: true };
  }

  const fileName = typeof file === "string" ? file : file?.name || "";
  const fileType = typeof file === "string" ? file : file?.type || "";

  const allowedExtensions = allowedTypes
    .filter((type) => type.startsWith("."))
    .map((type) => type.toLowerCase());

  const allowedMimeTypes = allowedTypes.filter((type) => !type.startsWith("."));

  const fileExtension = getFileExtension(fileName);

  // Check extension
  const isExtensionValid =
    allowedExtensions.length === 0 || allowedExtensions.includes(fileExtension);

  // Check MIME type
  const isMimeTypeValid =
    allowedMimeTypes.length === 0 ||
    allowedMimeTypes.some((type) => {
      if (type.endsWith("/*")) {
        return fileType.startsWith(type.replace("/*", ""));
      }
      return fileType === type;
    });

  if (!isExtensionValid && !isMimeTypeValid) {
    // Provide specific error message for PDF-only validation
    const isPdfOnly =
      allowedTypes.length === 2 &&
      allowedTypes.includes("application/pdf") &&
      allowedTypes.includes(".pdf");

    const message = isPdfOnly
      ? `Only PDF files are supported. "${
          fileExtension || fileType
        }" is not allowed.`
      : `File type "${fileExtension || fileType}" is not supported.`;

    return {
      valid: false,
      message,
    };
  }

  return { valid: true };
};

/**
 * Trigger a browser download of a text string as a file
 * @param {string} content - Text content to download
 * @param {string} filename - Name for the downloaded file
 * @param {string} mimeType - MIME type for the blob
 *
 * @example
 * downloadTextFile("# Hello", "notes.md", "text/markdown;charset=utf-8;")
 */
export const downloadTextFile = (
  content,
  filename,
  mimeType = "text/plain;charset=utf-8;"
) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export default {
  MAX_DOCUMENT_UPLOAD_MB,
  formatFileSize,
  getFileIcon,
  getDocumentIconName,
  getIconExtension,
  truncateFileName,
  truncateMiddle,
  getFileExtension,
  isViewableFile,
  validateFileSize,
  validateFileType,
  downloadTextFile,
};
